import {Howl, Howler} from "howler";
import * as PIXI from "pixi.js";
import {PredefinedPIXIAnchorPoint} from "../PredefinedPIXIAnchorPoint";
import {VideoPokerHand} from "./api/serverResponseClasses/VideoPokerHand";
import {CardAnimation} from "./CardAnimation";
import data from "./configs/data";
import {Button} from "./controlElements/Button";

export class CardsSimulation extends PIXI.Container {

    private readonly cardsPositions: Array<PIXI.Point> = [new PIXI.Point(219, 410), new PIXI.Point(430, 410),
        new PIXI.Point(640, 410), new PIXI.Point(851, 410), new PIXI.Point(1063, 410)];

    private readonly holdButtonsPositions: Array<PIXI.Point> = [new PIXI.Point(219, 542),
        new PIXI.Point(430, 542), new PIXI.Point(640, 542), new PIXI.Point(851, 542),
        new PIXI.Point(1063, 542)];

    private readonly CARDS_AMOUNT = 5;

    private cardsAnimations: Array<CardAnimation> = [];
    private holdButtons: Array<Button> = [];
    private invisibleHoldButtons: Array<Button> = [];
    private lockerSymbols: Array<PIXI.Sprite> = [];
    private cardsLockStates: Array<boolean> = [false, false, false, false, false];

    private currentVideoPokerHand: VideoPokerHand;
    private cardsNormalDealingSpeedSound: Howl;
    private cardsFastDealingSpeedSound: Howl;
    private holdOffSound: Howl;
    private holdOnSound: Howl;

    constructor() {
        super();
        this.createAndAddCardsAnimations();
        this.createAndAddLockerSymbols();
        this.createAndAddHoldButtons();
        this.createAndAddInvisibleHoldButtons();
        this.unlockAllCards();

        this.cardsNormalDealingSpeedSound = new Howl({
            src: [data.audios.dealingNormalSpeed],
            volume: 0.7,
        });
        this.cardsFastDealingSpeedSound = new Howl({
            src: [data.audios.dealingFastSpeed2],
            volume: 0.7,
        });
        this.holdOffSound = new Howl({
            src: [data.audios.holdOff],
            volume: 0.7,
        });
        this.holdOnSound = new Howl({
            src: [data.audios.holdOn],
            volume: 0.7,
        });
    }

    public async showCards(videoPokerHand: VideoPokerHand, fastAnimationMode: boolean): Promise<void> {
        const cards = videoPokerHand.getCards();
        if (fastAnimationMode) {
            this.cardsFastDealingSpeedSound.play();
            for (let i = 0; i < cards.length; i++) {
                this.cardsAnimations[i].setCardFront(cards[i].getColor(), cards[i].getName());
                this.cardsAnimations[i].uncoverCardFast();
            }
        } else {
            for (let i = 0; i < cards.length; i++) {
                this.cardsNormalDealingSpeedSound.play();
                this.cardsAnimations[i].setCardFront(cards[i].getColor(), cards[i].getName());
                await this.cardsAnimations[i].uncoverCard();
            }
        }
        this.currentVideoPokerHand = videoPokerHand;
    }

    public hideNotLockedCards(): void {
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            if (!this.cardsLockStates[i]) {
                this.cardsAnimations[i].showCoveredCard();
            }
        }
    }

    public async changeCards(videoPokerHand: VideoPokerHand, fastAnimationMode: boolean): Promise<void> {
        const currentCards = this.currentVideoPokerHand.getCards();
        const newCards = videoPokerHand.getCards();
        if (fastAnimationMode) {
            for (let i = 0; i < newCards.length; i++) {
                if (currentCards[i].getColor() !== newCards[i].getColor() ||
                    currentCards[i].getName() !== newCards[i].getName()) {
                    this.cardsAnimations[i].setCardFront(newCards[i].getColor(), newCards[i].getName());
                    if (i === (newCards.length - 1)) {
                        this.cardsFastDealingSpeedSound.play();
                        await this.cardsAnimations[i].uncoverCardFast();
                    } else {
                        this.cardsAnimations[i].uncoverCardFast();
                    }
                }
            }
        } else {
            for (let i = 0; i < newCards.length; i++) {
                if (currentCards[i].getColor() !== newCards[i].getColor() ||
                    currentCards[i].getName() !== newCards[i].getName()) {
                    this.cardsNormalDealingSpeedSound.play();
                    this.cardsAnimations[i].setCardFront(newCards[i].getColor(), newCards[i].getName());
                    await this.cardsAnimations[i].uncoverCard();
                }
            }
        }
        this.currentVideoPokerHand = videoPokerHand;
    }

    public hideCards(): void {
        for (let i = 0; i < this.cardsAnimations.length; i++) {
            this.cardsAnimations[i].showCoveredCard();
        }
    }

    public getNotLockedCardsIndexes(): Array<number> {
        const notLockedIndexes: Array<number> = [];
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            if (!this.cardsLockStates[i]) {
                notLockedIndexes.push(i);
            }
        }
        return notLockedIndexes;
    }

    public enableButtons(): void {
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            this.holdButtons[i].enabled = true;
        }
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            this.invisibleHoldButtons[i].enabled = true;
        }
    }

    public disableButtons(): void {
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            this.holdButtons[i].enabled = false;
        }
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            this.invisibleHoldButtons[i].enabled = false;
        }
    }

    public lockCards(cardsIndexes: Array<number>): void {
        this.unlockAllCards();
        for (let i = 0; i < cardsIndexes.length; i++) {
            this.lockerSymbols[cardsIndexes[i]].visible = true;
            this.cardsLockStates[cardsIndexes[i]] = true;
        }
    }

    public unlockAllCards(): void {
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            this.lockerSymbols[i].visible = false;
            this.cardsLockStates[i] = false;
        }
    }

    public hideLockerSymbols(): void {
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            this.lockerSymbols[i].visible = false;
        }
    }

    private createAndAddCardsAnimations(): void {
        let cardAnimation: CardAnimation;
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            cardAnimation = new CardAnimation();
            cardAnimation.position = this.cardsPositions[i];
            cardAnimation.showCoveredCard();
            this.cardsAnimations.push(cardAnimation);
            this.addChild(cardAnimation);
        }
    }

    private createAndAddLockerSymbols(): void {
        let lockerSymbol: PIXI.Sprite;
        for (let i = 0; i < 5; i++) {
            lockerSymbol = PIXI.Sprite.fromImage(data.images.heldLockerSymbol);
            lockerSymbol.anchor = PredefinedPIXIAnchorPoint.CENTER;
            lockerSymbol.position = this.cardsPositions[i];
            this.lockerSymbols.push(lockerSymbol);
            this.addChild(lockerSymbol);
        }
    }

    private createAndAddHoldButtons(): void {
        let button: Button;
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            button = new Button(data.images.holdBase, data.images.holdHover, data.images.holdClick,
                data.images.holdInActive, data.audios.buttonClicked);
            button.position = this.holdButtonsPositions[i];
            button.subscribeOnClicked(this.onHoldButtonClicked.bind(this, i));
            this.holdButtons.push(button);
            this.addChild(button);
        }
    }

    private createAndAddInvisibleHoldButtons(): void {
        let button: Button;
        for (let i = 0; i < this.CARDS_AMOUNT; i++) {
            button = new Button(data.images.invisibleCardClickArea, data.images.invisibleCardClickArea,
                data.images.invisibleCardClickArea, data.images.invisibleCardClickArea, data.audios.buttonClicked);
            button.position = this.cardsPositions[i];
            button.subscribeOnClicked(this.onHoldButtonClicked.bind(this, i));
            this.invisibleHoldButtons.push(button);
            this.addChild(button);
        }
    }

    private onHoldButtonClicked(buttonIndex: number): void {
        if (this.cardsLockStates[buttonIndex]) {
            this.holdOffSound.play();
        } else {
            this.holdOnSound.play();
        }
        this.cardsLockStates[buttonIndex] = !this.cardsLockStates[buttonIndex];
        this.lockerSymbols[buttonIndex].visible = !this.lockerSymbols[buttonIndex].visible;
    }
}



// WEBPACK FOOTER //
// ./src/app/CardsSimulation.ts